import { dateFormatter } from './calendarFormatters';
function validTimezone(timezone) {
  try {
    dateFormatter('en-US', { timeZone: timezone }).format(new Date(0));
    return true;
  } catch (_) {
    return false;
  }
}

function localParts(instant, timezone) {
  const parts = dateFormatter('en-CA', {
    timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23'
  }).formatToParts(new Date(instant));
  return Object.fromEntries(parts.filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
}

function timezoneOffsetMs(instant, timezone) {
  const p = localParts(instant, timezone);
  return Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) -
    Math.floor(new Date(instant).getTime() / 1000) * 1000;
}

function zonedLocalToUtc(parts, timezone, preference = 'earliest') {
  const localEpoch = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour || 0, parts.minute || 0, parts.second || 0);
  const offsets = new Set();
  for (const delta of [-172800000, -86400000, 0, 86400000, 172800000]) {
    offsets.add(timezoneOffsetMs(localEpoch + delta, timezone));
  }
  const candidates = [...offsets].map(offset => localEpoch - offset).filter(candidate => {
    const actual = localParts(candidate, timezone);
    return actual.year === parts.year && actual.month === parts.month && actual.day === parts.day &&
      actual.hour === (parts.hour || 0) && actual.minute === (parts.minute || 0) && actual.second === (parts.second || 0);
  }).sort((a, b) => a - b);
  if (!candidates.length) throw new RangeError('Local calendar boundary does not exist in timezone');
  return preference === 'latest' ? candidates[candidates.length - 1] : candidates[0];
}

function addLocal(parts, kind, amount = 1) {
  const date = new Date(Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour || 0, parts.minute || 0, parts.second || 0));
  if (kind === 'day') date.setUTCDate(date.getUTCDate() + amount);
  else if (kind === 'month') date.setUTCMonth(date.getUTCMonth() + amount);
  else if (kind === 'year') date.setUTCFullYear(date.getUTCFullYear() + amount);
  return {
    year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate(),
    hour: date.getUTCHours(), minute: date.getUTCMinutes(), second: date.getUTCSeconds()
  };
}

/** Calendar-aware period boundaries. Hour/minute follow the actual UTC timeline,
 * so both repeated fall-back hours remain distinct. Day/month/year use local
 * calendar midnight and therefore naturally produce 23/25-hour DST days. */
function periodContaining(instant, periodKind, timezone = 'UTC', options = {}) {
  if (!validTimezone(timezone)) throw new RangeError('Invalid IANA timezone');
  const time = new Date(instant).getTime();
  if (!Number.isFinite(time)) throw new TypeError('A valid instant is required');
  const local = localParts(time, timezone);
  let start;
  let end;
  if (periodKind === 'minute') {
    start = time - local.second * 1000 - (time % 1000 + 1000) % 1000;
    end = start + 60000;
  } else if (periodKind === 'hour') {
    start = time - (local.minute * 60 + local.second) * 1000 - (time % 1000 + 1000) % 1000;
    end = start + 3600000;
  } else {
    let base;
    if (periodKind === 'day') base = { year: local.year, month: local.month, day: local.day };
    else if (periodKind === 'month') base = { year: local.year, month: local.month, day: 1 };
    else if (periodKind === 'year') base = { year: local.year, month: 1, day: 1 };
    else if (periodKind === 'billing_period') {
      const billingDay = Math.min(Math.max(Number(options.billingStartDay) || 1, 1), 28);
      base = { year: local.year, month: local.month, day: billingDay };
      let candidate = zonedLocalToUtc(base, timezone);
      if (candidate > time) base = addLocal(base, 'month', -1);
    } else throw new RangeError(`Unsupported period kind: ${periodKind}`);
    start = zonedLocalToUtc(base, timezone);
    end = zonedLocalToUtc(addLocal(base, periodKind === 'billing_period' ? 'month' : periodKind), timezone);
  }
  return { periodStart: new Date(start).toISOString(), periodEnd: new Date(end).toISOString() };
}

function demandIntervalContaining(instant, timezone = 'UTC', intervalMinutes = 15) {
  if (!validTimezone(timezone)) throw new RangeError('Invalid IANA timezone');
  if (!Number.isInteger(intervalMinutes) || intervalMinutes < 1 || intervalMinutes > 1440) {
    throw new RangeError('Demand interval must be between 1 and 1440 minutes');
  }
  const time = new Date(instant).getTime();
  if (!Number.isFinite(time)) throw new TypeError('A valid instant is required');
  const local = localParts(time, timezone);
  // Common sub-hour tariff intervals stay on the actual UTC timeline. This
  // keeps both copies of a repeated fall-back hour distinct while retaining
  // local :00/:15/:30/:45 alignment.
  if (intervalMinutes <= 60 && 60 % intervalMinutes === 0) {
    const elapsedSeconds = (local.minute % intervalMinutes) * 60 + local.second;
    const start = time - elapsedSeconds * 1000 - (time % 1000 + 1000) % 1000;
    return {
      periodStart: new Date(start).toISOString(),
      periodEnd: new Date(start + intervalMinutes * 60000).toISOString()
    };
  }
  const localDay = Math.floor(Date.UTC(local.year, local.month - 1, local.day) / 86400000);
  const serialMinute = localDay * 1440 + local.hour * 60 + local.minute;
  const startSerialMinute = Math.floor(serialMinute / intervalMinutes) * intervalMinutes;
  const startLocal = new Date(startSerialMinute * 60000);
  const startParts = {
    year: startLocal.getUTCFullYear(), month: startLocal.getUTCMonth() + 1,
    day: startLocal.getUTCDate(), hour: startLocal.getUTCHours(),
    minute: startLocal.getUTCMinutes(), second: 0
  };
  let start;
  try {
    const earliest = zonedLocalToUtc(startParts, timezone, 'earliest');
    const latest = zonedLocalToUtc(startParts, timezone, 'latest');
    const durationMs = intervalMinutes * 60000;
    const matching = [...new Set([earliest, latest])]
      .filter(candidate => candidate <= time && time < candidate + durationMs)
      .sort((left, right) => right - left);
    // Prefer the occurrence that actually contains the input instant. This is
    // what keeps repeated fall-back wall times in distinct elapsed intervals.
    start = matching[0] ?? (latest <= time ? latest : earliest);
  } catch (_) {
    // A tariff boundary may land in a spring-forward gap. Preserve a fixed
    // duration and select the first actual instant after the missing wall time.
    const elapsedSeconds = (serialMinute - startSerialMinute) * 60 + local.second;
    start = time - elapsedSeconds * 1000 - (time % 1000 + 1000) % 1000;
  }
  return {
    periodStart: new Date(start).toISOString(),
    periodEnd: new Date(start + intervalMinutes * 60000).toISOString()
  };
}

export { periodContaining, demandIntervalContaining };
